import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

// services
import projectService from "services/projects.js";
import aiModelService from "services/aiModels.js";
import logger from "utils/logger";
import { useAuth } from "contexts/AuthContext";

export default function CardProjectForm({ project, isOpen, onSave, onCancel }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    project_code: '',
    project_name: '',
    enabled: true,
    descripcion: '',
    publico_objetivo: '',
    definiciones_tecnicas: '',
    design_definitions: '',
    key_features: '',
    objetivos: '',
    production_url: '',
    github_repository: '',
    main_branch: '',
    backlog_limit: 5,
    agents: [],
    environment: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [availableAgents, setAvailableAgents] = useState({});
  const [aiModels, setAiModels] = useState([]);
  const [taskStatuses] = useState(['created', 'pending', 'working', 'reviewing', 'finished', 'failed']);
  const modalContentRef = React.useRef(null);
  const fieldRefs = React.useRef({
    project_code: null,
    project_name: null,
    github_repository: null,
    main_branch: null,
    production_url: null
  });

  // Compute plan limit status
  const planStatus = useMemo(() => {
    if (!user) return null;

    const maxProjects = user.max_enabled_projects ?? -1;
    const currentProjects = user.projects_count ?? 0;
    const planName = user.plan?.name || 'Sin plan';
    const isUnlimited = maxProjects === -1;

    if (isUnlimited) {
      return {
        type: 'unlimited',
        planName,
        currentProjects,
        maxProjects,
      };
    }

    const percentage = maxProjects > 0 ? (currentProjects / maxProjects) * 100 : 0;
    const isAtLimit = currentProjects >= maxProjects;
    const isNearLimit = percentage >= 80 && !isAtLimit;

    return {
      type: isAtLimit ? 'at_limit' : isNearLimit ? 'near_limit' : 'ok',
      planName,
      currentProjects,
      maxProjects,
      percentage,
    };
  }, [user]);

  // In create mode, disable form if user reached plan limit
  const isCreateDisabled = !project && planStatus?.type === 'at_limit';

  // Load available agents and AI models
  useEffect(() => {
    const loadAgents = async () => {
      try {
        const response = await projectService.getAvailableAgents();
        if (response.success) {
          setAvailableAgents(response.data);
        }
      } catch (error) {
        logger.error('Error loading agents:', error);
      }
    };
    const loadAiModels = async () => {
      try {
        const response = await aiModelService.getActiveModels();
        if (response.success) {
          setAiModels(response.data || []);
        }
      } catch (error) {
        logger.error('Error loading AI models:', error);
      }
    };
    loadAgents();
    loadAiModels();
  }, []);

  // Reset form when modal opens/closes or project changes
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';

      if (project) {
        // Edit mode - populate form with project data
        setFormData({
          project_code: project.project_code || '',
          project_name: project.project_name || '',
          enabled: project.enabled !== undefined ? project.enabled : true,
          descripcion: project.descripcion || '',
          publico_objetivo: project.publico_objetivo || '',
          definiciones_tecnicas: project.definiciones_tecnicas || '',
          design_definitions: project.design_definitions || '',
          key_features: project.key_features || '',
          objetivos: project.objetivos || '',
          production_url: project.production_url || '',
          github_repository: project.github_repository || '',
          main_branch: project.main_branch || 'main',
          backlog_limit: project.backlog_limit || 5,
          agents: (project.agents || []).map(a => ({
            ...a,
            model_id: a.ai_model ? a.model_id : null
          })),
          environment: project.environment || ''
        });
      } else {
        // Create mode - reset form
        setFormData({
          project_code: '',
          project_name: '',
          enabled: true,
          descripcion: '',
          publico_objetivo: '',
          definiciones_tecnicas: '',
          design_definitions: '',
          key_features: '',
          objetivos: '',
          production_url: '',
          github_repository: '',
          main_branch: 'main',
          backlog_limit: 5,
          agents: [],
          environment: ''
        });
      }
      setErrors({});
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [project, isOpen]);

  // Handle ESC key press to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onCancel]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.project_code.trim()) {
      newErrors.project_code = 'El código del proyecto es requerido';
    }

    if (!formData.project_name.trim()) {
      newErrors.project_name = 'El nombre del proyecto es requerido';
    }

    if (!formData.github_repository.trim()) {
      newErrors.github_repository = 'El repositorio de GitHub es requerido';
    } else if (!/^https:\/\/github\.com\/[\w\-\.]+\/[\w\-\.]+\.git$/.test(formData.github_repository)) {
      newErrors.github_repository = 'Debe ser una URL HTTPS válida de GitHub que termine en .git (ej: https://github.com/usuario/repositorio.git)';
    }

    if (!formData.main_branch.trim()) {
      newErrors.main_branch = 'La rama principal es requerida';
    }

    if (formData.production_url && !/^https?:\/\/.+/.test(formData.production_url)) {
      newErrors.production_url = 'Debe ser una URL válida (ej: https://ejemplo.com)';
    }

    setErrors(newErrors);

    // Scroll to top of modal and focus first field with error if there are errors
    if (Object.keys(newErrors).length > 0) {
      if (modalContentRef.current) {
        modalContentRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }

      // Focus first field with error for accessibility
      const firstErrorField = Object.keys(newErrors)[0];
      if (fieldRefs.current[firstErrorField]) {
        setTimeout(() => {
          fieldRefs.current[firstErrorField]?.focus();
        }, 300); // Wait for scroll animation
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let result;
      if (project) {
        // Update existing project
        result = await projectService.updateProject(project.id, formData);
      } else {
        // Create new project
        result = await projectService.createProject(formData);
      }

      if (onSave) {
        onSave(result);
      }
    } catch (error) {
      logger.error('Error saving project:', error);

      // Handle plan limit exceeded error
      if (error.response?.data?.error?.type === 'plan_limit_exceeded') {
        const errorData = error.response.data.error;
        setErrors({
          general: error.response.data.message || `Has alcanzado el límite de proyectos de tu plan ${errorData.plan}. ${project ? 'Desactiva un proyecto existente o actualiza' : 'Actualiza'} tu plan para ${project ? 'activar' : 'crear'} más proyectos.`
        });
      }
      // Handle validation errors from API
      else if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({
          general: 'Error al guardar el proyecto. Por favor, inténtalo de nuevo.'
        });
      }

      // Scroll to top of modal to show errors and focus first error field
      if (modalContentRef.current) {
        modalContentRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }

      // Focus first field with error for accessibility
      const firstErrorField = Object.keys(errors)[0];
      if (fieldRefs.current[firstErrorField]) {
        setTimeout(() => {
          fieldRefs.current[firstErrorField]?.focus();
        }, 300); // Wait for scroll animation
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      project_code: '',
      project_name: '',
      enabled: true,
      descripcion: '',
      publico_objetivo: '',
      definiciones_tecnicas: '',
      design_definitions: '',
      key_features: '',
      objetivos: '',
      production_url: '',
      github_repository: '',
      main_branch: 'main',
      backlog_limit: 5,
      agents: [],
      environment: ''
    });
    setErrors({});
    onCancel();
  };

  // Handle agent selection
  const handleAgentToggle = (agentKey) => {
    setFormData(prev => {
      const existingAgent = prev.agents.find(a => a.agent === agentKey);

      if (existingAgent) {
        // Remove agent
        return {
          ...prev,
          agents: prev.agents.filter(a => a.agent !== agentKey)
        };
      } else {
        // Add agent with appropriate default task_status
        // MAITE gets 'reviewing', all other agents get 'created'
        const defaultTaskStatus = agentKey === 'MAITE' ? 'reviewing' : 'created';

        return {
          ...prev,
          agents: [...prev.agents, {
            agent: agentKey,
            postcommand: '',
            task_status: defaultTaskStatus,
            model_id: null
          }]
        };
      }
    });
  };

  // Handle agent field change
  const handleAgentFieldChange = (agentKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      agents: prev.agents.map(a =>
        a.agent === agentKey ? { ...a, [field]: value } : a
      )
    }));
  };

  if (!isOpen) return null;

  // Agregar estilos CSS para animación
  const spinnerStyle = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  return createPortal(
    <>
      <style>{spinnerStyle}</style>
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}
    >
      <div
        ref={modalContentRef}
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflowY: 'auto',
          zIndex: 100000,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '1.5rem 1.5rem 1rem',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: '#374151' }}>
            {project ? 'Editar Proyecto' : 'Nuevo Proyecto'}
          </h3>
          <button
            onClick={handleCancel}
            style={{ 
              background: 'none', 
              border: 'none', 
              fontSize: '1.5rem', 
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0.25rem'
            }}
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
          {/* Disabled indicator at the top */}
          {project && !formData.enabled && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem',
              backgroundColor: '#fef2f2',
              border: '1px solid #fca5a5',
              color: '#dc2626',
              borderRadius: '0.375rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ fontSize: '1.25rem' }}>⚠️</span>
              <span style={{ fontWeight: 'bold' }}>Este proyecto está deshabilitado</span>
            </div>
          )}

          {errors.general && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              borderRadius: '0.375rem'
            }}>
              {errors.general}
            </div>
          )}

          {/* Plan limit banners - only in create mode */}
          {!project && planStatus?.type === 'at_limit' && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              backgroundColor: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '0.375rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.25rem' }}>🚫</span>
                <span style={{ fontWeight: 'bold', color: '#dc2626', fontSize: '0.875rem' }}>
                  Límite de proyectos alcanzado
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.8125rem', color: '#991b1b' }}>
                Has alcanzado el límite de {planStatus.maxProjects} proyecto{planStatus.maxProjects !== 1 ? 's' : ''} de tu plan <strong>{planStatus.planName}</strong>. Actualiza tu plan para crear más proyectos.
              </p>
              <a
                href="/plans"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  padding: '0.375rem 0.75rem',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  borderRadius: '0.375rem',
                  fontSize: '0.8125rem',
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  alignSelf: 'flex-start'
                }}
              >
                Ver Planes
              </a>
            </div>
          )}

          {!project && planStatus?.type === 'near_limit' && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              backgroundColor: '#fffbeb',
              border: '1px solid #fcd34d',
              borderRadius: '0.375rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.25rem' }}>⚠️</span>
                <span style={{ fontWeight: 'bold', color: '#92400e', fontSize: '0.875rem' }}>
                  Cerca del límite de proyectos
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.8125rem', color: '#78350f' }}>
                Estás usando {planStatus.currentProjects} de {planStatus.maxProjects} proyectos permitidos en tu plan <strong>{planStatus.planName}</strong>. Considera actualizar tu plan si necesitas más proyectos.
              </p>
              <a
                href="/plans"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  color: '#92400e',
                  fontSize: '0.8125rem',
                  fontWeight: 'bold',
                  textDecoration: 'underline',
                  alignSelf: 'flex-start'
                }}
              >
                Conocer otros planes
              </a>
            </div>
          )}

          {!project && planStatus?.type === 'unlimited' && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              backgroundColor: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '0.375rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ fontSize: '1rem' }}>✅</span>
              <p style={{ margin: 0, fontSize: '0.8125rem', color: '#166534' }}>
                Tienes {planStatus.currentProjects} proyecto{planStatus.currentProjects !== 1 ? 's' : ''} activo{planStatus.currentProjects !== 1 ? 's' : ''}. Tu plan <strong>{planStatus.planName}</strong> permite proyectos ilimitados.
              </p>
            </div>
          )}

          {/* Enabled Checkbox */}
          <div style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: loading ? 'not-allowed' : 'pointer',
              gap: '0.75rem'
            }}>
              <input
                type="checkbox"
                name="enabled"
                checked={formData.enabled}
                onChange={handleInputChange}
                disabled={loading}
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              />
              <div>
                <span style={{
                  color: '#374151',
                  fontSize: '0.875rem',
                  fontWeight: 'bold'
                }}>
                  Proyecto Habilitado
                </span>
                <p style={{
                  color: '#6b7280',
                  fontSize: '0.75rem',
                  marginTop: '0.25rem',
                  marginBottom: 0
                }}>
                  Desmarca esta opción para deshabilitar el proyecto temporalmente
                </p>
              </div>
            </label>
          </div>

          {/* Project Code */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              color: '#374151', 
              fontSize: '0.875rem', 
              fontWeight: 'bold', 
              marginBottom: '0.5rem' 
            }}>
              Código del Proyecto *
            </label>
            <input
              ref={(el) => fieldRefs.current.project_code = el}
              type="text"
              name="project_code"
              value={formData.project_code}
              onChange={handleInputChange}
              placeholder="Ej: PROJ-001"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.project_code ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white'
              }}
            />
            {errors.project_code && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.project_code}
              </p>
            )}
          </div>

          {/* Project Name */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              color: '#374151', 
              fontSize: '0.875rem', 
              fontWeight: 'bold', 
              marginBottom: '0.5rem' 
            }}>
              Nombre del Proyecto *
            </label>
            <input
              ref={(el) => fieldRefs.current.project_name = el}
              type="text"
              name="project_name"
              value={formData.project_name}
              onChange={handleInputChange}
              placeholder="Nombre descriptivo del proyecto"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.project_name ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white'
              }}
            />
            {errors.project_name && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.project_name}
              </p>
            )}
          </div>

          {/* Production URL */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              color: '#374151', 
              fontSize: '0.875rem', 
              fontWeight: 'bold', 
              marginBottom: '0.5rem' 
            }}>
              URL de Producción
            </label>
            <input
              ref={(el) => fieldRefs.current.production_url = el}
              type="url"
              name="production_url"
              value={formData.production_url}
              onChange={handleInputChange}
              placeholder="https://ejemplo.com"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.production_url ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white'
              }}
            />
            {errors.production_url && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.production_url}
              </p>
            )}
          </div>

          {/* GitHub Repository */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem'
            }}>
              Repositorio de GitHub *
            </label>

            {/* GitHub App Notice */}
            <div style={{
              marginBottom: '0.75rem',
              padding: '0.75rem',
              backgroundColor: '#eff6ff',
              border: '1px solid #3b82f6',
              borderRadius: '0.375rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <span style={{ fontSize: '1rem' }}>ℹ️</span>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#1e40af', fontWeight: 'bold' }}>
                    Requisito: Instalación de GitHub App
                  </p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#1e3a8a' }}>
                    Para que BEDCEO pueda acceder a tu repositorio, debes instalar la{' '}
                    <a
                      href="https://github.com/apps/bedceo"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#2563eb', fontWeight: 'bold', textDecoration: 'underline' }}
                    >
                      GitHub App de BEDCEO
                    </a>
                    .{' '}
                    <a
                      href="/github-app-guide"
                      target="_blank"
                      style={{ color: '#2563eb', fontWeight: 'bold', textDecoration: 'underline' }}
                    >
                      Ver guía de instalación
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <input
              ref={(el) => fieldRefs.current.github_repository = el}
              name="github_repository"
              value={formData.github_repository}
              onChange={handleInputChange}
              placeholder="https://github.com/usuario/repositorio.git"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.github_repository ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white'
              }}
            />
            {errors.github_repository && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.github_repository}
              </p>
            )}
          </div>

          {/* Main Branch */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              color: '#374151', 
              fontSize: '0.875rem', 
              fontWeight: 'bold', 
              marginBottom: '0.5rem' 
            }}>
              Rama Principal *
            </label>
            <input
              ref={(el) => fieldRefs.current.main_branch = el}
              name="main_branch"
              value={formData.main_branch}
              onChange={handleInputChange}
              placeholder="main"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.main_branch ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white'
              }}
            />
            {errors.main_branch && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.main_branch}
              </p>
            )}
          </div>

          {/* Backlog Limit */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem'
            }}>
              Límite de Backlog
            </label>
            <input
              type="number"
              name="backlog_limit"
              value={formData.backlog_limit}
              onChange={handleInputChange}
              min="1"
              max="100"
              placeholder="5"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.backlog_limit ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white'
              }}
            />
            {errors.backlog_limit && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.backlog_limit}
              </p>
            )}
            <p style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem' }}>
              Número de tareas que puede contener el backlog (por defecto: 5)
            </p>
          </div>

          {/* Description */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              color: '#374151', 
              fontSize: '0.875rem', 
              fontWeight: 'bold', 
              marginBottom: '0.5rem' 
            }}>
              Descripción
            </label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleInputChange}
              placeholder="Descripción del proyecto"
              rows="3"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.descripcion ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white',
                resize: 'vertical'
              }}
            />
            {errors.descripcion && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.descripcion}
              </p>
            )}
          </div>

          {/* Target Audience */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              color: '#374151', 
              fontSize: '0.875rem', 
              fontWeight: 'bold', 
              marginBottom: '0.5rem' 
            }}>
              Público Objetivo
            </label>
            <textarea
              name="publico_objetivo"
              value={formData.publico_objetivo}
              onChange={handleInputChange}
              placeholder="Descripción del público objetivo del proyecto"
              rows="3"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.publico_objetivo ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white',
                resize: 'vertical'
              }}
            />
            {errors.publico_objetivo && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.publico_objetivo}
              </p>
            )}
          </div>

          {/* Technical Definitions */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem'
            }}>
              Definiciones Técnicas
            </label>
            <textarea
              name="definiciones_tecnicas"
              value={formData.definiciones_tecnicas}
              onChange={handleInputChange}
              placeholder="Definiciones y especificaciones técnicas del proyecto"
              rows="4"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.definiciones_tecnicas ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white',
                resize: 'vertical'
              }}
            />
            {errors.definiciones_tecnicas && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.definiciones_tecnicas}
              </p>
            )}
          </div>

          {/* Design Definitions */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem'
            }}>
              Definiciones de Diseño
            </label>
            <textarea
              name="design_definitions"
              value={formData.design_definitions}
              onChange={handleInputChange}
              placeholder="Definiciones y especificaciones de diseño del proyecto"
              rows="4"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.design_definitions ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white',
                resize: 'vertical'
              }}
            />
            {errors.design_definitions && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.design_definitions}
              </p>
            )}
          </div>

          {/* Key Features */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              color: '#374151', 
              fontSize: '0.875rem', 
              fontWeight: 'bold', 
              marginBottom: '0.5rem' 
            }}>
              Características Clave
            </label>
            <textarea
              name="key_features"
              value={formData.key_features}
              onChange={handleInputChange}
              placeholder="Características principales y funcionalidades clave"
              rows="4"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.key_features ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white',
                resize: 'vertical'
              }}
            />
            {errors.key_features && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.key_features}
              </p>
            )}
          </div>

          {/* Objectives */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem'
            }}>
              Objetivos
            </label>
            <textarea
              name="objetivos"
              value={formData.objetivos}
              onChange={handleInputChange}
              placeholder="Objetivos principales del proyecto"
              rows="3"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.objetivos ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white',
                resize: 'vertical'
              }}
            />
            {errors.objetivos && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.objetivos}
              </p>
            )}
          </div>

          {/* Environment */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem'
            }}>
              Variables de Entorno
            </label>
            <textarea
              name="environment"
              value={formData.environment}
              onChange={handleInputChange}
              placeholder="Variables de entorno del proyecto (ej: NODE_ENV=production, API_KEY=...)"
              rows="4"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.environment ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out',
                backgroundColor: loading ? '#f3f4f6' : 'white',
                resize: 'vertical',
                fontFamily: 'monospace'
              }}
            />
            {errors.environment && (
              <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {errors.environment}
              </p>
            )}
          </div>

          {/* Agents Section */}
          <div style={{ marginBottom: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              marginBottom: '0.75rem'
            }}>
              Agentes Asignados
            </label>
            <p style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '1rem' }}>
              Selecciona los agentes que trabajarán en este proyecto
            </p>

            {Object.keys(availableAgents).length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {Object.entries(availableAgents).map(([agentKey, agentDescription]) => {
                  const isSelected = formData.agents.some(a => a.agent === agentKey);
                  const agentData = formData.agents.find(a => a.agent === agentKey) || {};

                  return (
                    <div
                      key={agentKey}
                      style={{
                        border: isSelected ? '2px solid #10b981' : '1px solid #d1d5db',
                        borderRadius: '0.5rem',
                        padding: '1rem',
                        backgroundColor: isSelected ? '#f0fdf4' : 'white'
                      }}
                    >
                      {/* Agent checkbox */}
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: isSelected ? '0.75rem' : '0' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleAgentToggle(agentKey)}
                          disabled={loading}
                          style={{
                            marginRight: '0.5rem',
                            width: '1.125rem',
                            height: '1.125rem',
                            cursor: loading ? 'not-allowed' : 'pointer'
                          }}
                        />
                        <div>
                          <span style={{ fontWeight: 'bold', color: '#374151', fontSize: '0.875rem' }}>
                            {agentKey}
                          </span>
                          <span style={{ color: '#6b7280', fontSize: '0.75rem', marginLeft: '0.5rem' }}>
                            ({agentDescription})
                          </span>
                        </div>
                      </div>

                      {/* Agent configuration fields (shown when selected) */}
                      {isSelected && (
                        <div style={{ marginLeft: '1.625rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {/* Task Status */}
                          <div>
                            <label style={{
                              display: 'block',
                              color: '#374151',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              marginBottom: '0.25rem'
                            }}>
                              Estado de Tarea
                            </label>
                            <select
                              value={agentData.task_status || 'created'}
                              onChange={(e) => handleAgentFieldChange(agentKey, 'task_status', e.target.value)}
                              disabled={loading}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                outline: 'none',
                                backgroundColor: loading ? '#f3f4f6' : 'white'
                              }}
                            >
                              {taskStatuses.map(status => (
                                <option key={status} value={status}>
                                  {status}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* AI Model */}
                          <div>
                            <label style={{
                              display: 'block',
                              color: '#374151',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              marginBottom: '0.25rem'
                            }}>
                              Modelo de IA
                            </label>
                            <select
                              value={agentData.model_id || ''}
                              onChange={(e) => handleAgentFieldChange(agentKey, 'model_id', e.target.value ? parseInt(e.target.value) : null)}
                              disabled={loading}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                outline: 'none',
                                backgroundColor: loading ? '#f3f4f6' : 'white'
                              }}
                            >
                              <option value="">Sin modelo asignado</option>
                              {aiModels.map(m => (
                                <option key={m.id} value={m.id}>
                                  {m.name}{m.provider ? ` (${m.provider})` : ''}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Post Command */}
                          <div>
                            <label style={{
                              display: 'block',
                              color: '#374151',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              marginBottom: '0.25rem'
                            }}>
                              Post Command (opcional)
                            </label>
                            <textarea
                              value={agentData.postcommand || ''}
                              onChange={(e) => handleAgentFieldChange(agentKey, 'postcommand', e.target.value)}
                              placeholder="Comando a ejecutar después de la tarea"
                              rows="2"
                              disabled={loading}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                outline: 'none',
                                backgroundColor: loading ? '#f3f4f6' : 'white',
                                resize: 'vertical'
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ color: '#6b7280', fontSize: '0.75rem', fontStyle: 'italic' }}>
                Cargando agentes disponibles...
              </p>
            )}
          </div>

          {/* Buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '0.75rem',
            paddingTop: '1rem',
            borderTop: '1px solid #e5e7eb'
          }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                textTransform: 'uppercase',
                opacity: loading ? 0.5 : 1
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || isCreateDisabled}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: (loading || isCreateDisabled) ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                cursor: (loading || isCreateDisabled) ? 'not-allowed' : 'pointer',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? (
                <>
                  <span style={{
                    display: 'inline-block',
                    width: '1rem',
                    height: '1rem',
                    border: '2px solid #ffffff',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Guardando...
                </>
              ) : (
                <>
                  {project ? 'Actualizar' : 'Crear'} Proyecto
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>,
    document.body
  );
}

CardProjectForm.propTypes = {
  project: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};